import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { X, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { money, listMoney, usdApprox, useCurrency } from "@/lib/currency";

export const CartDrawer = () => {
  useCurrency(); // re-render prices on display-currency change
  const { items, removeItem, updateQty, open, setOpen, subtotal, recurringSubtotal, recurringLabel } = useCart();
  // Display-only US$ list-price subtotal; undefined (→ exact conversion)
  // when any line lacks an approved US$ price. AWG `subtotal` is
  // authoritative.
  const usdSubtotal = items.every((it) => Number(it.price_usd) > 0)
    ? items.reduce((sum, it) => sum + Number(it.price_usd) * (it.qty || 1), 0)
    : undefined;
  const navigate = useNavigate();

  if (!open) return null;

  return (
    <div data-testid="cart-drawer" className="fixed inset-0 z-[100] flex">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <aside className="relative ml-auto w-full max-w-md h-full bg-[#0F0F0F] border-l border-[#262626] flex flex-col">
        <div className="p-6 border-b border-[#262626] flex items-center justify-between">
          <div>
            <div className="ur-eyebrow">Your Cart</div>
            <h3 className="font-display text-2xl font-bold mt-1">{items.length} items</h3>
          </div>
          <button
            data-testid="cart-close-btn"
            onClick={() => setOpen(false)}
            className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center hover:border-[#FF6600]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-20">
              <div className="w-16 h-16 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center mb-4">
                <ShoppingBag className="w-6 h-6 text-[#A3A3A3]" />
              </div>
              <p className="text-[#A3A3A3]">Your cart is empty</p>
            </div>
          )}
          {items.map((it) => (
            <div
              key={it.item_id}
              data-testid={`cart-item-${it.item_id}`}
              className="ur-card p-4 flex gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-[#FF6600] font-bold">
                      {it.kind}
                    </div>
                    <div className="font-semibold text-white mt-1 truncate">{it.name}</div>
                  </div>
                  <button
                    data-testid={`cart-remove-${it.item_id}`}
                    onClick={() => removeItem(it.item_id)}
                    className="text-[#A3A3A3] hover:text-[#FF6600]"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {it.meta?.tier && (
                  <div className="mt-2 text-xs text-[#A3A3A3]">
                    Tier: <span className="text-white">{it.meta.tier}</span>
                  </div>
                )}
                {it.meta?.addons?.length > 0 && (
                  <div className="mt-1 text-xs text-[#A3A3A3]">
                    Add-ons: <span className="text-white">{it.meta.addons.join(", ")}</span>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  {/* P0 — quantity is captured inside the configurator and
                      is authoritative server-side. A drawer stepper here
                      would let the customer see one total and pay another
                      (tiered synthetics + tier lines with max=1 would
                      diverge). Show quantity as read-only; to change it,
                      remove and reconfigure the line. */}
                  <div className="text-xs text-[#A3A3A3]" data-testid="cart-line-qty">
                    Qty <span className="text-white ml-1">{it.quantity || it.qty || 1}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-white leading-none">
                      {listMoney(it.price * (it.qty || 1), it.price_usd ? it.price_usd * (it.qty || 1) : undefined)}
                    </div>
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5">{usdApprox(it.price * (it.qty || 1))}</div>
                    {it.recurring_price > 0 && (
                      <div
                        data-testid={`cart-line-recurring-${it.item_id}`}
                        className="text-[11px] text-[#FF6600] mt-1 whitespace-nowrap"
                      >
                        + {listMoney(it.recurring_price, it.recurring_price_usd)}/{it.recurring_label || "month"} ongoing
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-6 border-t border-[#262626] space-y-4 bg-[#0A0A0A]">
          <div className="flex items-center justify-between">
            <span className="text-[#A3A3A3]">Subtotal (due now)</span>
            <div className="text-right">
              <span
                data-testid="cart-subtotal"
                className="font-display text-2xl font-bold text-white block leading-none"
              >
                {money(subtotal, usdSubtotal)}
              </span>
              <span className="text-[11px] text-[#A3A3A3] mt-1 inline-block">{usdApprox(subtotal)}</span>
            </div>
          </div>
          {recurringSubtotal > 0 && (
            <div
              data-testid="cart-recurring-subtotal"
              className="flex items-center justify-between pt-3 border-t border-[#262626]"
            >
              <span className="text-[#A3A3A3] text-sm">
                Ongoing Website Care
              </span>
              <span className="text-[#FF6600] font-semibold whitespace-nowrap">
                {money(recurringSubtotal)}/{recurringLabel || "month"}
              </span>
            </div>
          )}
          <button
            data-testid="cart-checkout-btn"
            disabled={items.length === 0}
            onClick={() => {
              setOpen(false);
              navigate("/checkout");
            }}
            className="ur-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Checkout <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </aside>
    </div>
  );
};
